import React, { useState, useRef, useCallback, useMemo } from "react";
import { X, Plus, Tag as TagIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  predefinedTags?: string[];
  placeholder?: string;
  maxTags?: number;
  disabled?: boolean;
  className?: string;
}

export const TagInput: React.FC<TagInputProps> = ({
  value = [],
  onChange,
  predefinedTags = [],
  placeholder = "Add tags...",
  maxTags,
  disabled = false,
  className,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter predefined tags to show only those not already selected (memoized)
  const availablePredefinedTags = useMemo(
    () => predefinedTags.filter((tag) => !value.includes(tag)),
    [predefinedTags, value],
  );

  // Check if max tags limit is reached
  const isMaxTagsReached = useMemo(
    () => maxTags !== undefined && value.length >= maxTags,
    [maxTags, value.length],
  );

  // Add a tag (memoized)
  const addTag = useCallback(
    (tag: string) => {
      const trimmedTag = tag.trim();

      // Early returns for invalid cases
      if (!trimmedTag || value.includes(trimmedTag) || isMaxTagsReached) {
        setInputValue("");
        return;
      }

      onChange([...value, trimmedTag]);
      setInputValue("");
    },
    [value, onChange, isMaxTagsReached],
  );

  // Remove a tag (memoized)
  const removeTag = useCallback(
    (tagToRemove: string) => {
      onChange(value.filter((tag) => tag !== tagToRemove));
    },
    [value, onChange],
  );

  // Handle input key down (memoized)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        addTag(inputValue);
      } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
        removeTag(value[value.length - 1]);
      }
    },
    [inputValue, value, addTag, removeTag],
  );

  // Handle input blur (memoized)
  const handleBlur = useCallback(() => {
    if (inputValue.trim()) {
      addTag(inputValue);
    }
  }, [inputValue, addTag]);

  // Handle predefined tag selection (memoized)
  const handlePredefinedTagClick = useCallback(
    (tag: string) => {
      addTag(tag);
      setIsPopoverOpen(false);
    },
    [addTag],
  );

  return (
    <div className={cn("space-y-2", className)}>
      {/* Selected Tags */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="pl-2 pr-1 py-1 text-xs"
            >
              {tag}
              {!disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                  onClick={() => removeTag(tag)}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </Badge>
          ))}
        </div>
      )}

      {/* Input and Popover */}
      {!disabled && (!maxTags || value.length < maxTags) && (
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              type="text"
              placeholder={placeholder}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              disabled={disabled}
              className="flex-1"
            />
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={disabled}
              >
                <TagIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
          </div>

          <PopoverContent className="w-[300px] p-2" align="start">
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground px-2 py-1">
                Predefined Tags
              </p>
              {availablePredefinedTags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {availablePredefinedTags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="cursor-pointer hover:bg-secondary pl-2 pr-1 py-1"
                      onClick={() => handlePredefinedTagClick(tag)}
                    >
                      {tag}
                      <Plus className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground px-2 py-2">
                  All predefined tags are already selected
                </p>
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Help Text */}
      <p className="text-xs text-muted-foreground">
        {maxTags
          ? `${value.length}/${maxTags} tags selected. Press Enter or comma to add a tag.`
          : "Press Enter or comma to add a tag."}
      </p>
    </div>
  );
};
